import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ChangeEvent, FocusEvent, KeyboardEvent, useState } from "react";
import {
  Box,
  Drawer,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Avatar,
  Typography,
  IconButton,
} from "@mui/material";

import { getSearchValueRoute } from "../../../../routers/routeUtils";

import { logout } from "../../../../firebase/firebase";
import APP_ROUTES from "../../../../routers/routeConstants";
import { useProfileData } from "../../../../hooks/useProfileData";
import { deletePushNotifications } from "../../../../api/notifications";

import AnimatedIcon from "./AnimatedIcon";
import { MenuItemArrowIcon, GoalngoMobileLogo, SearchIcon, LogoutIcon } from "../../assets/icons/layout-icons";

type NavigationItem = {
  label: string;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  to?: string;
  children?: NavigationItem[];
};

const MobileSideNav = ({
  mobileOpen,
  handleCloseMobileNav,
  navigationList,
}: {
  mobileOpen: boolean;
  handleCloseMobileNav: () => void;
  navigationList: NavigationItem[];
}) => {
  const { t, i18n } = useTranslation("layout");
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    navigationList.forEach((item) => {
      const hasActiveChild = item.children?.some((child) => {
        const childPath = child.to ? new URL(child.to, "http://dummy.com").pathname : undefined;
        return !!childPath && childPath === location.pathname;
      });
      if (hasActiveChild) map[item.label] = true;
    });
    return map;
  });

  const isPathActive = (to?: string) => {
    if (!to) return false;
    try {
      return new URL(to, "http://dummy.com").pathname === location.pathname;
    } catch {
      return false;
    }
  };

  const isParentActive = (item: NavigationItem) => {
    if (isPathActive(item.to)) return true;
    return !!item.children?.some((c) => isPathActive(c.to));
  };

  const handleToggle = (label: string) => {
    setOpenMap((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleNavigate = (to?: string) => {
    if (!to) return;
    navigate(to);
    handleCloseMobileNav();
  };

  const handleChangeText = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchValue(e.target.value);

  const handleNavigateToSearch = () => {
    if (searchValue.length > 0) navigate(getSearchValueRoute(searchValue));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleNavigateToSearch();
      handleCloseMobileNav();
    }
  };

  const handleOnFocus = (e: FocusEvent<HTMLInputElement>) => e.stopPropagation();

  const { data: userData, isLoading } = useProfileData();
  const { fullName, email, media, firstTwoChr } = userData;

  const myQueryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      localStorage.removeItem("entityId");
      deletePushNotifications().catch(console.warn);
      await logout();
      myQueryClient.clear();
      navigate(APP_ROUTES.HOME);
      window.location.reload();
    } catch (error) {
      console.error("Error during logout:", error);
      localStorage.clear();
      window.location.href = APP_ROUTES.HOME;
    }
  };

  return (
    <Drawer
      // anchor={isArabic ? "right" : "left"}
      open={mobileOpen}
      onClose={handleCloseMobileNav}
      ModalProps={{ keepMounted: true }}
      sx={styles.container}
    >
      <Box role="presentation" sx={styles.content}>
        <GoalngoMobileLogo />
        <TextField
          value={searchValue}
          onChange={handleChangeText}
          placeholder={t("search")}
          onKeyDown={handleKeyDown}
          onFocus={handleOnFocus}
          InputProps={{
            startAdornment: <SearchIcon />,
            autoComplete: "new-password",
          }}
          variant="outlined"
          sx={styles.textField}
        />
        <Box sx={styles.navigationList}>
          <List component="nav" aria-labelledby="mobile-sidenav">
            {navigationList.map((item) => {
              const hasChildren = !!item.children && item.children.length > 0;
              const parentSelected = isParentActive(item);

              return (
                <Box key={item.label} component="div">
                  <ListItemButton
                    onClick={() => (hasChildren ? handleToggle(item.label) : handleNavigate(item.to))}
                    sx={styles.menuItemButton(parentSelected)}
                  >
                    {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "space-between" }}>
                      <ListItemText primary={t(item.label)} sx={{ m: 0 }} />
                      {item.endIcon && item.endIcon}
                    </Box>
                    {hasChildren && (
                      <AnimatedIcon>
                        {openMap[item.label] ? (
                          <MenuItemArrowIcon sx={{ transform: "rotate(180deg)" }} />
                        ) : (
                          <MenuItemArrowIcon />
                        )}
                      </AnimatedIcon>
                    )}
                  </ListItemButton>
                  {hasChildren && (
                    <Collapse in={!!openMap[item.label]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={styles.menuItemCollapse}>
                        {item.children!.map((child) => (
                          <ListItemButton
                            key={child.label}
                            onClick={() => handleNavigate(child.to)}
                            sx={styles.menuSubItemButton(isPathActive(child.to))}
                            disabled={!child.to}
                          >
                            {child.icon && <ListItemIcon>{child.icon}</ListItemIcon>}
                            <ListItemText primary={t(child.label)} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Box>
        <Divider />
        <Box sx={styles.userInfoWrapper}>
          <Box sx={styles.userInfo}>
            <Avatar src={media} sx={styles.avatar}>
              {firstTwoChr}
            </Avatar>
            <Box sx={styles.userInfoText}>
              <Typography sx={styles.fullName}>{fullName}</Typography>
              <Typography sx={styles.email}>{email}</Typography>
            </Box>
          </Box>
          <IconButton onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileSideNav;

const styles = {
  container: {
    maxHeight: "100vh",
    display: { xs: "block", lg: "none" },
    "& .MuiBackdrop-root": {
      height: "100vh",
      maxHeight: "100vh",
    },
    "& .MuiDrawer-paper": {
      boxSizing: "border-box",
      width: "280px",
      background: "var(--Base-White, #FFF)",
      boxShadow: "0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03)",
      height: "100dvh",
      maxHeight: "100dvh",
      display: "flex",
    },
  },
  content: {
    p: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    height: "100%",
    minHeight: 0,
  },
  textField: {
    width: "100%",
    maxWidth: "100%",
    "& .MuiOutlinedInput-root": {
      padding: "0 20px",
    },
    "& .MuiInputBase-root": {
      columnGap: "8px",
      borderRadius: "8px",
      border: "1px solid var(--Gray-300, #D0D5DD)",
      background: "var(--Base-White, #FFF)",
      boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    },
  },
  navigationList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
  menuItemButton: (isActive: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "8px 12px",
    "& .MuiListItemIcon-root": {
      minWidth: "unset",
      mr: "12px",
    },
    "& .MuiListItemText-root": {
      m: 0,
      "& .MuiTypography-root": {
        color: isActive ? "var(--Gray-800, #182230)" : "var(--Gray-700, #344054)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: isActive ? 500 : 400,
        lineHeight: "24px",
      },
    },
  }),
  menuSubItemButton: (isActive: boolean) => ({
    padding: "8px 12px 8px 48px",
    "& .MuiListItemText-root": {
      "& .MuiTypography-root": {
        color: isActive ? "var(--Gray-800, #182230)" : "var(--Gray-700, #344054)",
        fontFamily: "Inter",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: isActive ? 500 : 400,
        lineHeight: "24px",
      },
    },
  }),
  menuItemCollapse: {
    padding: "0",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    maxWidth: "calc(100% - 40px)",
  },
  avatar: {
    width: "40px",
    height: "40px",
  },
  fullName: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  email: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userInfoText: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxWidth: "100%",
  },
  userInfoWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
};
